import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  completeCredentialSetup,
  validateCredentialSetup,
} from "../../api/clientPortalPublic";
import { formatApiError } from "../../utils/formatApiError";

export default function PortalSetupCredentials() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();
  const salonIdParam =
    (searchParams.get("salonId") || searchParams.get("salon_id") || "").trim();
  const salonId = useMemo(() => Number(salonIdParam || 0), [salonIdParam]);
  const portalBasePath = salonIdParam ? `/portal/salon/${salonIdParam}` : "/portal/setup";
  const portalLoginPath = salonIdParam
    ? `/portal/salon/${salonIdParam}/login`
    : "/login";

  const [loading, setLoading] = useState(true);
  const [validInfo, setValidInfo] = useState(null);
  const [error, setError] = useState("");
  const [duplicateEmail, setDuplicateEmail] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token || !salonId) {
        setLoading(false);
        setValidInfo(null);
        return;
      }
      setLoading(true);
      setError("");
      setDuplicateEmail(false);
      try {
        const data = await validateCredentialSetup(token, salonId);
        if (!active) return;
        if (data?.valid) {
          setValidInfo(data);
        } else {
          setValidInfo(null);
        }
      } catch (err) {
        if (!active) return;
        if (err?.response?.status === 409) {
          setDuplicateEmail(true);
        }
        setValidInfo(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [salonId, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!username.trim()) {
      setError("Username is required.");
      return;
    }
    if (username.trim().length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await completeCredentialSetup({
        salon_id: salonId,
        token,
        username: username.trim(),
        password,
      });
      setSuccess(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Portal setup error:", err);
      }
      if (err?.response?.status === 409) {
        setDuplicateEmail(true);
        setError("");
        return;
      }
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const showInvalid = !loading && !validInfo && !duplicateEmail;

  return (
    <div className="px-4 py-6">
      <div className="mx-auto w-full max-w-[1400px] text-base md:text-lg">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Client Portal
            </h1>
            <p className="text-sm md:text-base text-rose-100">
              Account Setup
            </p>
          </div>
          <div className="px-6 py-8">
            <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-6">
              {loading && (
                <div className="text-sm text-slate-600">Loading...</div>
              )}

              {showInvalid && (
                <div className="text-center">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                    This setup link is invalid or expired.
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    Please request a new setup email from the salon.
                  </p>
                </div>
              )}

              {!loading && duplicateEmail && (
                <div className="text-center">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                    Email needs to be updated
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    This email is used by more than one client profile at this
                    salon. Please contact the salon to update your email before
                    activating your online account.
                  </p>
                  <div className="mt-4">
                    <Link
                      to={salonId ? `/portal/salon/${salonId}/login` : "/login"}
                      className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Back to Login
                    </Link>
                  </div>
                </div>
              )}

              {!loading && validInfo && success && (
                <div className="text-center">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                    All set!
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    You can sign in now. Rewards and Gift Cards will appear once
                    the salon activates your access. If it's not activated within
                    48 hours, please contact the salon or ask at your appointment.
                  </p>
                  <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to={portalLoginPath}
                      className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800"
                    >
                      Sign In
                    </Link>
                    <Link
                      to={portalBasePath}
                      className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Back to Portal Home
                    </Link>
                  </div>
                </div>
              )}

              {!loading && validInfo && !success && !duplicateEmail && (
                <div>
                  <div className="space-y-2">
                    <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                      Set up your account
                    </h2>
                    <p className="text-sm md:text-base text-slate-600">
                      Create your username and password to finish setup.
                    </p>
                    {validInfo?.email_masked && (
                      <p className="text-xs text-slate-500">
                        Email on file: {validInfo.email_masked}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-600">
                        Username
                      </label>
                      <input
                        type="text"
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 px-4"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-600">
                        Password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 px-4"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-600">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 px-4"
                        required
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="submit"
                        disabled={saving}
                        className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {saving ? "Saving..." : "Save Credentials"}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
